import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileDown, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/quote/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Dieses Dokument wurde nicht gefunden oder der Link ist ungültig."));
  }, [token]);

  const pdfUrl = (docType) => `${API}/api/public/quote/${token}/pdf?doc_type=${docType}`;

  return (
    <div className="min-h-screen bg-zinc-100 flex items-start justify-center px-4 py-10" data-testid="public-quote-page">
      <div className="w-full max-w-xl bg-white border border-zinc-200 shadow-sm">
        <div className="px-6 py-5 border-b-2 border-zinc-900 flex items-center gap-3">
          <img src="/brand/mak-logo.png" alt="" className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div>
            <div className="text-sm font-semibold text-zinc-900" data-testid="public-company">{data?.company || "MAK Konfigurator"}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ihr Dokument</div>
          </div>
        </div>
        <div className="px-6 py-6">
          {error && <div className="text-sm text-red-600" data-testid="public-error">{error}</div>}
          {!data && !error && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 size={14} className="animate-spin"/> Wird geladen…</div>}
          {data && (
            <>
              <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight" data-testid="public-title">Angebot {data.quoteNumber}</h1>
              <div className="text-sm text-zinc-600 mt-1">
                {data.customerName && <span>für {data.customerName} · </span>}
                <span>vom {fmtDate(data.createdAt)}</span>
                {data.validUntil && <span> · gültig bis {fmtDate(data.validUntil)}</span>}
              </div>
              {data.projectName && <div className="text-sm text-zinc-600">Projekt: {data.projectName}</div>}
              {data.totals && (
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-zinc-50 border border-zinc-200 px-3 py-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Netto</div><div className="font-mono">{fmtEUR(data.totals.total)}</div></div>
                  <div className="bg-zinc-900 text-white px-3 py-2"><div className="text-[10px] uppercase tracking-widest text-zinc-300">Gesamt brutto</div><div className="font-mono font-semibold" data-testid="public-brutto">{fmtEUR(data.totals.bruttoTotal)}</div></div>
                </div>
              )}
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <a href={pdfUrl("angebot")} target="_blank" rel="noreferrer" data-testid="public-pdf-angebot"
                   className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-900 text-white text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-zinc-800">
                  <FileDown size={14}/> Angebot als PDF
                </a>
                {data.confirmationNumber && (
                  <a href={pdfUrl("confirmation")} target="_blank" rel="noreferrer" data-testid="public-pdf-confirmation"
                     className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-900 text-zinc-900 text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-zinc-50">
                    <FileDown size={14}/> {data.confirmationNumber} (AB / Proforma)
                  </a>
                )}
              </div>
              <div className="mt-6 text-xs text-zinc-500">{data.contact}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
